import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { Button } from '@components/common'
import { useProjectWizardStore } from '@store/useProjectWizardStore'
import projectDocumentService from '@services/projectDocumentService'
import '../ProjectWizard.scss'

/**
 * Step 3: Document Upload
 *
 * Allows uploading supporting documents (MSA, SOW, Contracts, etc.)
 */
const DocumentUploadStep = ({ onValidChange }) => {
	const { documents, addDocument, removeDocument, uploadProgress } = useProjectWizardStore()

	const [dragActive, setDragActive] = useState(false)
	const [selectedType, setSelectedType] = useState('other')
	const [error, setError] = useState(null)
	const fileInputRef = useRef(null)

	const documentTypes = projectDocumentService.getDocumentTypes()

	// Handle drag events
	const handleDrag = (e) => {
		e.preventDefault()
		e.stopPropagation()
		if (e.type === 'dragenter' || e.type === 'dragover') {
			setDragActive(true)
		} else if (e.type === 'dragleave') {
			setDragActive(false)
		}
	}

	// Handle drop
	const handleDrop = (e) => {
		e.preventDefault()
		e.stopPropagation()
		setDragActive(false)

		const files = e.dataTransfer?.files
		if (files && files.length > 0) {
			handleFiles(Array.from(files))
		}
	}

	// Handle file selection
	const handleFileSelect = (e) => {
		const files = e.target.files
		if (files && files.length > 0) {
			handleFiles(Array.from(files))
		}
		// Reset input
		if (fileInputRef.current) {
			fileInputRef.current.value = ''
		}
	}

	// Process selected files
	const handleFiles = (files) => {
		setError(null)

		files.forEach((file) => {
			// Validate file
			const validation = projectDocumentService.validateFile(file)
			if (!validation.valid) {
				setError(validation.errors.join('. '))
				return
			}

			// Check if file already exists
			const existingDoc = documents.find(
				(d) => d.file.name === file.name && d.file.size === file.size
			)
			if (existingDoc) {
				setError(`File "${file.name}" is already added`)
				return
			}

			// Add document to store
			const documentId = `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
			addDocument({
				id: documentId,
				file,
				type: selectedType,
				name: file.name,
				size: file.size,
				status: 'pending', // Will be 'uploading', 'uploaded', 'error' during actual upload
			})
		})
	}

	// Handle document removal
	const handleRemove = (documentId) => {
		removeDocument(documentId)
	}

	// Handle type change for a document
	const handleTypeChange = (documentId, newType) => {
		const store = useProjectWizardStore.getState()
		store.updateDocument(documentId, { type: newType })
	}

	// Group documents by type
	const groupedDocuments = documents.reduce((acc, doc) => {
		const type = doc.type || 'other'
		if (!acc[type]) {
			acc[type] = []
		}
		acc[type].push(doc)
		return acc
	}, {})

	// Documents are optional, so always valid
	React.useEffect(() => {
		onValidChange && onValidChange(true)
	}, [onValidChange])

	return (
		<div className="wizard-step">
			<div className="wizard-step__header">
				<h2 className="wizard-step__title">Upload Documents</h2>
				<p className="wizard-step__description">
					Upload supporting documents for your compliance project (optional)
				</p>
			</div>

			<div className="wizard-step__content">
				{/* Document Type Selector */}
				<div className="form-group">
					<label className="form-label">Document Type</label>
					<select
						className="form-select"
						value={selectedType}
						onChange={(e) => setSelectedType(e.target.value)}
					>
						{documentTypes.map((type) => (
							<option key={type.value} value={type.value}>
								{type.label}
							</option>
						))}
					</select>
				</div>

				{/* Upload Area */}
				<div
					className={`upload-zone ${dragActive ? 'upload-zone--active' : ''}`}
					onDragEnter={handleDrag}
					onDragLeave={handleDrag}
					onDragOver={handleDrag}
					onDrop={handleDrop}
					onClick={() => fileInputRef.current?.click()}
				>
					<input
						ref={fileInputRef}
						type="file"
						accept=".pdf,.doc,.docx"
						multiple
						onChange={handleFileSelect}
						className="upload-zone__input"
					/>
					<div className="upload-zone__content">
						<i className="icon-cloud-upload upload-zone__icon" />
						<p className="upload-zone__title">
							Drag & drop files here or click to browse
						</p>
						<p className="upload-zone__subtitle">
							Accepted formats: PDF, DOC, DOCX (Max 10MB each)
						</p>
					</div>
				</div>

				{/* Error Message */}
				{error && <div className="upload-error">{error}</div>}

				{/* Document List */}
				{documents.length > 0 && (
					<div className="document-list">
						<h4 className="document-list__title">
							Uploaded Documents ({documents.length})
						</h4>

						{Object.entries(groupedDocuments).map(([type, docs]) => (
							<div key={type} className="document-group">
								<h5 className="document-group__title">
									{projectDocumentService.getTypeLabel(type)}
								</h5>
								<div className="document-group__items">
									{docs.map((doc) => (
										<div key={doc.id} className="document-item">
											<div className="document-item__icon">
												<i className="icon-doc" />
											</div>
											<div className="document-item__info">
												<span className="document-item__name">{doc.name}</span>
												<span className="document-item__size">
													{projectDocumentService.formatFileSize(doc.size)}
												</span>
											</div>
											<div className="document-item__type">
												<select
													value={doc.type}
													onChange={(e) => handleTypeChange(doc.id, e.target.value)}
													className="document-item__type-select"
												>
													{documentTypes.map((t) => (
														<option key={t.value} value={t.value}>
															{t.label}
														</option>
													))}
												</select>
											</div>
											{uploadProgress[doc.id] !== undefined && (
												<div className="document-item__progress">
													<div
														className="document-item__progress-bar"
														style={{ width: `${uploadProgress[doc.id]}%` }}
													/>
												</div>
											)}
											<Button
												variant="ghost"
												size="small"
												onClick={() => handleRemove(doc.id)}
												className="document-item__remove"
												aria-label="Remove document"
											>
												<i className="icon-trash" />
											</Button>
										</div>
									))}
								</div>
							</div>
						))}
					</div>
				)}

				{/* Info Box */}
				<div className="info-box">
					<i className="icon-info info-box__icon" />
					<div className="info-box__content">
						<strong>Supported Document Types:</strong>
						<ul>
							<li>MSA - Master Service Agreement</li>
							<li>SOW - Statement of Work</li>
							<li>Purchase Order</li>
							<li>Contract</li>
							<li>Security Questionnaire</li>
							<li>Other supporting documents</li>
						</ul>
					</div>
				</div>
			</div>
		</div>
	)
}

DocumentUploadStep.propTypes = {
	onValidChange: PropTypes.func,
}

export default DocumentUploadStep
